/**
 * ReceiptConfirmModal Component
 * Modal for confirming receipt of powers with a 4-character code
 * Features case-sensitive validation and admin override support
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner, OathAlert } from '../../../shared/OathUI';
import { toast } from 'react-toastify';
import { confirmPowerReceipt } from '../../../../services/oathPowerReportService';
import { RECEIPT_VALIDATION, RECEIPT_MESSAGES } from '../utils/constants';

const ReceiptConfirmModal = ({
  isOpen,
  onClose,
  onSuccess,
  agentId = null,
  agencyName = ''
}) => {
  const [confirmationCode, setConfirmationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const inputRef = useRef(null);

  // Determine if input should be masked (admin override code starts with "code")
  const shouldMaskInput = confirmationCode.toLowerCase().startsWith(RECEIPT_VALIDATION.ADMIN_CODE_PREFIX);

  // Validate code format
  const isValidCode = useCallback((code) => {
    // Accept standard 4-char code or admin override code
    if (RECEIPT_VALIDATION.CODE_PATTERN.test(code)) {
      return true;
    }
    // Admin override code format: "code-XXXXXX"
    if (code.toLowerCase().startsWith('code-') && code.length >= 6) {
      return true;
    }
    return false;
  }, []);

  const codeIsValid = isValidCode(confirmationCode);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setConfirmationCode('');
      setError('');
      setShowHelp(false);
    }
  }, [isOpen]);

  // Handle code input change with length validation
  const handleCodeChange = (e) => {
    const value = e.target.value;

    // Enforce length limits:
    // - If typing admin code (starts with "code"), allow up to 11 chars
    // - Otherwise, limit to 4 characters
    const isAdminCodePrefix = value.toLowerCase().startsWith(RECEIPT_VALIDATION.ADMIN_CODE_PREFIX);
    const maxLen = isAdminCodePrefix ? RECEIPT_VALIDATION.ADMIN_CODE_LENGTH : RECEIPT_VALIDATION.CODE_LENGTH;

    if (value.length > maxLen) {
      return; // Don't allow input beyond max length
    }

    setConfirmationCode(value);
    setError(''); // Clear error when typing
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!codeIsValid) {
      setError(RECEIPT_MESSAGES.INVALID_CODE);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Always send uppercase to match database comparison
      const codeToSend = confirmationCode.toUpperCase();
      const result = await confirmPowerReceipt(codeToSend, agentId);

      if (result.success) {
        const count = result.data?.confirmedCount || 1;
        toast.success(RECEIPT_MESSAGES.SUCCESS(count));
        onSuccess?.(result.data);
        onClose();
      } else {
        setError(result.error || RECEIPT_MESSAGES.INVALID_CODE);
      }
    } catch (err) {
      console.error('Error confirming receipt:', err);
      setError(RECEIPT_MESSAGES.NETWORK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && codeIsValid && !loading) {
      handleSubmit(e);
    }
  };

  const handleCancel = () => {
    setConfirmationCode('');
    setError('');
    setShowHelp(false);
    onClose();
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Confirm Receipt of Powers"
      size="md"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleSubmit}
            disabled={loading || !codeIsValid}
            icon={loading ? null : CheckCircleIcon}
            className="w-full sm:w-auto"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <OathSpinner size="sm" />
                Confirming...
              </span>
            ) : (
              'Confirm Receipt'
            )}
          </OathButton>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Admin context banner */}
        {agentId && agencyName && (
          <OathAlert variant="info" className="mb-4">
            <div className="flex items-center gap-2">
              <InformationCircleIcon className="h-5 w-5" />
              <span>
                Confirming on behalf of: <strong>{agencyName}</strong>
              </span>
            </div>
          </OathAlert>
        )}

        {/* Case-sensitive warning */}
        <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <ExclamationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            {RECEIPT_MESSAGES.CASE_SENSITIVE_WARNING}
          </p>
        </div>

        {/* Code input */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label
              htmlFor="confirmationCode"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Confirmation Code
            </label>
            <button
              type="button"
              onClick={() => setShowHelp(!showHelp)}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Show help"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </button>
          </div>

          {showHelp && (
            <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {RECEIPT_MESSAGES.CODE_HELP}
              </p>
            </div>
          )}

          <input
            ref={inputRef}
            id="confirmationCode"
            type={shouldMaskInput ? 'password' : 'text'}
            value={confirmationCode}
            onChange={handleCodeChange}
            onKeyDown={handleKeyDown}
            placeholder="Enter code"
            maxLength={shouldMaskInput ? RECEIPT_VALIDATION.ADMIN_CODE_LENGTH : RECEIPT_VALIDATION.CODE_LENGTH}
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck="false"
            disabled={loading}
            className={`w-full px-4 py-6 text-center text-4xl font-mono font-bold tracking-[0.5em] uppercase border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors placeholder:text-gray-300 dark:placeholder:text-gray-600 placeholder:tracking-normal placeholder:text-lg placeholder:font-normal ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{
              letterSpacing: shouldMaskInput ? 'normal' : '0.5em',
              fontSize: shouldMaskInput ? '1.5rem' : '2.25rem'
            }}
          />

          {/* Character count indicator */}
          <div className="mt-2 flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
            <span>
              {confirmationCode.length > 0 && !shouldMaskInput && (
                <>
                  {confirmationCode.length} / {RECEIPT_VALIDATION.CODE_LENGTH} characters
                </>
              )}
            </span>
            {codeIsValid && (
              <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                <CheckCircleIcon className="h-4 w-4" />
                Valid format
              </span>
            )}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <ExclamationCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}
      </form>
    </OathModal>
  );
};

export default ReceiptConfirmModal;
